import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaTrashAlt } from "react-icons/fa";
import axios from "axios";
import Cookies from "js-cookie";
import api from "../../api/api";
import "../../styles/Form.css";

const LEAVE_APPLY_MESSAGE =
  "Leave this application and return to the job list? You can choose a different position there.";

const ApplyJobPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({
    is_over_21: null,
    has_class1_license: null,
    has_twic_card: null,
    eligible_fast_card: null,
    has_1yr_experience: null,
    live_in_canada: null,
    mountain_experience_years: null,
    convicted_felony_7yrs: null,
    felony_date: null,
    felony_details: null,
    convicted_dui_dwi: null,
    dui_date: null,
    dui_details: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [resumeFiles, setResumeFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const token = localStorage.getItem("token");
  const BASE = process.env.REACT_APP_API_BASE_URL || "";
  const authHeader = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : undefined),
    [token]
  );

  const persistSelectedJob = useCallback(async () => {
    if (!token || !jobId) return;

    Cookies.set("pendingJobId", jobId, { path: "/", expires: 1 });

    try {
      await axios.post(
        `${BASE}/api/apply/selection`,
        { job_id: Number(jobId) },
        { headers: authHeader }
      );
    } catch (err) {
      console.error("Failed to persist selected job:", err);
    }
  }, [BASE, authHeader, jobId, token]);

  const clearSelectedJob = useCallback(async () => {
    Cookies.remove("pendingJobId");
    if (!token) return;

    try {
      await axios.delete(`${BASE}/api/apply/selection`, {
        headers: authHeader,
      });
    } catch (err) {
      console.error("Failed to clear selected job:", err);
    }
  }, [BASE, authHeader, token]);

  const handleLeaveApply = useCallback(async () => {
    const confirmed = window.confirm(LEAVE_APPLY_MESSAGE);
    if (!confirmed) return;

    await clearSelectedJob();
    navigate("/");
  }, [clearSelectedJob, navigate]);

  const loadResumeDocuments = useCallback(async () => {
    if (!token) return;

    try {
      const resumesRes = await axios.get(`${BASE}/api/apply/resume-documents`, {
        headers: authHeader,
      });
      setResumeFiles(resumesRes.data || []);
    } catch (err) {
      console.error("Failed to load resume documents:", err);
      setResumeFiles([]);
    }
  }, [BASE, authHeader, token]);

  useEffect(() => {
    axios
      .get(`${BASE}/api/open-positions/${jobId}`)
      .then((res) => setJob(res.data))
      .catch((err) => console.error("Job info load failed:", err));
  }, [BASE, jobId]);

  useEffect(() => {
    persistSelectedJob();
  }, [persistSelectedJob]);

  useEffect(() => {
    if (!token) return;

    let active = true;

    async function loadPageData() {
      try {
        const profileRes = await axios.get(`${BASE}/api/candidate/user/profile`, {
          headers: authHeader,
        });

        if (!active) return;
        setProfile(profileRes.data);
      } catch (err) {
        if (!active) return;
        console.error("Failed to load profile on apply page:", err);
        setProfile(null);
      }

      if (!active) return;
      await loadResumeDocuments();
    }

    loadPageData();

    return () => {
      active = false;
    };
  }, [BASE, authHeader, loadResumeDocuments, token]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];

    if (files.some((file) => !allowedTypes.includes(file.type))) {
      alert("Only PDF, JPG, or PNG files are allowed.");
      e.target.value = "";
      return;
    }

    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((file) => fd.append("files", file));

      await api.post("/api/apply/upload-resume", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      await loadResumeDocuments();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Resume upload failed.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleRemoveResume = async (resumeId) => {
    try {
      await api.delete(`/api/apply/resume-documents/${resumeId}`);
      setResumeFiles((prev) => prev.filter((file) => file.id !== resumeId));
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Failed to remove resume.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      form.is_over_21 === "0" ||
      form.has_class1_license === "0" ||
      form.has_1yr_experience === "0"
    ) {
      alert("Not eligible to apply.");
      setSubmitting(false);
      return;
    }

    if (form.convicted_felony_7yrs === "1" && !form.felony_date) {
      alert("Please provide the felony conviction date.");
      setSubmitting(false);
      return;
    }

    if (form.convicted_dui_dwi === "1" && !form.dui_date) {
      alert("Please provide the DUI conviction date.");
      setSubmitting(false);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        job_id: jobId,
        first_name: profile.first_name,
        last_name: profile.last_name,
        mobile: profile.mobile,
        home_phone: profile.home_phone,
        postal_code: profile.postal_code,
        ...Object.fromEntries(
          Object.entries(form).map(([k, v]) => {
            if (v === null || v === "") return [k, null];

            if (
              [
                "is_over_21",
                "has_class1_license",
                "has_twic_card",
                "eligible_fast_card",
                "has_1yr_experience",
                "live_in_canada",
                "convicted_felony_7yrs",
                "convicted_dui_dwi",
              ].includes(k)
            ) {
              return [k, v === "1" ? 1 : 0];
            }

            if (k === "mountain_experience_years") {
              return [k, Number(v)];
            }

            return [k, v];
          })
        ),
      };

      const { data } = await axios.post(`${BASE}/api/apply`, payload, {
        headers: authHeader,
      });

      Cookies.remove("pendingJobId");

      if (data.pipeline_status === 2) {
        alert("Application submitted. Continue onboarding.");
        navigate(`/candidate/onboarding/${data.application_id}`);
      } else if (data.pipeline_status === 3) {
        alert("You have already completed an application.");
        navigate("/");
      } else {
        alert(data.message || "Application submitted.");
        navigate(`/candidate/onboarding/${data.application_id}`);
      }
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Application failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!job) return <div>Loading job info...</div>;

  const questions = [
    { name: "is_over_21", label: "Are you at least 21 years old?" },
    {
      name: "has_class1_license",
      label: "Do you hold a Class 1 Driver's Licence?",
    },
    { name: "has_twic_card", label: "Do you have a TWIC card?" },
    { name: "eligible_fast_card", label: "Are you eligible for a FAST Card?" },
    {
      name: "has_1yr_experience",
      label:
        "Do you have at least 1 year of Semi-Tractor-Trailer driving experience in Canada/US?",
    },
    { name: "live_in_canada", label: "Do you currently live in Canada?" },
    {
      name: "convicted_felony_7yrs",
      label: "Have you been convicted of a felony in the last 7 years?",
    },
    {
      name: "convicted_dui_dwi",
      label: "Have you been convicted of DWI/DUI in the last 7 years?",
    },
  ];

  return (
    <div className="form-wrapper my-4">
      <h2>Apply for {job.p_position_title}</h2>
      <form onSubmit={handleSubmit}>
        {profile && (
          <fieldset className="border p-3 mb-4">
            <legend>Your Information</legend>
            <div className="row mb-3">
              <div className="col-md-4">
                <label>Email</label>
                <input
                  className="form-control"
                  disabled
                  value={profile.email || ""}
                />
              </div>
              <div className="col-md-4">
                <label>First Name</label>
                <input
                  className="form-control"
                  name="first_name"
                  value={profile.first_name || ""}
                  onChange={(e) =>
                    setProfile({ ...profile, first_name: e.target.value })
                  }
                />
              </div>
              <div className="col-md-4">
                <label>Last Name</label>
                <input
                  className="form-control"
                  name="last_name"
                  value={profile.last_name || ""}
                  onChange={(e) =>
                    setProfile({ ...profile, last_name: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-md-4">
                <label>Home Phone</label>
                <input
                  className="form-control"
                  name="home_phone"
                  value={profile.home_phone || ""}
                  onChange={(e) =>
                    setProfile({ ...profile, home_phone: e.target.value })
                  }
                />
              </div>
              <div className="col-md-4">
                <label>
                  Mobile Phone <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control"
                  name="mobile"
                  value={profile.mobile || ""}
                  onChange={(e) =>
                    setProfile({ ...profile, mobile: e.target.value })
                  }
                  required
                />
              </div>
              <div className="col-md-4">
                <label>
                  Postal Code <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control"
                  value={profile.postal_code || ""}
                  onChange={(e) =>
                    setProfile({
                      ...profile,
                      postal_code: e.target.value.toUpperCase(),
                    })
                  }
                  required
                  placeholder="A0A 0A0"
                  pattern="^[A-Za-z]\d[A-Za-z][ ]?\d[A-Za-z]\d$"
                  title="Please enter a valid postal code in the format A0A 0A0"
                />
              </div>
            </div>
          </fieldset>
        )}

        {[2, 8, 11].includes(job.p_category) && (
          <fieldset className="border p-3 mb-4">
            <legend>Application Questions</legend>
            {questions.map((q) => (
              <div className="mb-3" key={q.name}>
                <label className="d-block">
                  {q.label}
                  <span className="text-danger">*</span>
                </label>
                <div className="btn-group w-50" role="group">
                  <input
                    type="radio"
                    className="btn-check"
                    name={q.name}
                    value="1"
                    checked={form[q.name] === "1"}
                    onChange={handleChange}
                    required
                    id={`${q.name}_yes`}
                  />
                  <label
                    className="btn btn-outline-primary btn-lg px-4"
                    htmlFor={`${q.name}_yes`}
                  >
                    Yes
                  </label>

                  <input
                    type="radio"
                    className="btn-check"
                    name={q.name}
                    value="0"
                    checked={form[q.name] === "0"}
                    onChange={handleChange}
                    required
                    id={`${q.name}_no`}
                  />
                  <label
                    className="btn btn-outline-secondary btn-lg px-4"
                    htmlFor={`${q.name}_no`}
                  >
                    No
                  </label>
                </div>

                {q.name === "convicted_felony_7yrs" && form[q.name] === "1" && (
                  <>
                    <div className="row mt-2">
                      <div className="col-md-6">
                        <label>
                          Please select conviction date{" "}
                          <span className="text-danger">*</span>
                        </label>
                        <input
                          type="date"
                          name="felony_date"
                          value={form.felony_date || ""}
                          onChange={handleChange}
                          className="form-control mt-2"
                          required
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-6">
                        <label>
                          Details <span className="text-danger">*</span>
                        </label>
                        <textarea
                          className="form-control"
                          name="felony_details"
                          value={form.felony_details || ""}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>
                  </>
                )}

                {q.name === "convicted_dui_dwi" && form[q.name] === "1" && (
                  <>
                    <div className="row mt-2">
                      <div className="col-md-6">
                        <label>
                          Please select conviction date{" "}
                          <span className="text-danger">*</span>
                        </label>
                        <input
                          type="date"
                          name="dui_date"
                          value={form.dui_date || ""}
                          onChange={handleChange}
                          className="form-control mt-2"
                          required
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-6">
                        <label>
                          Details <span className="text-danger">*</span>
                        </label>
                        <textarea
                          className="form-control"
                          name="dui_details"
                          value={form.dui_details || ""}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}

            <div className="row mt-3">
              <div className="col-md-6">
                <label>
                  How many years of Mountain Experience?{" "}
                  <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  name="mountain_experience_years"
                  value={form.mountain_experience_years ?? ""}
                  onChange={handleChange}
                  min="0"
                  className="form-control"
                  required
                />
              </div>
            </div>
          </fieldset>
        )}

        <fieldset className="border p-3 mb-4">
          <legend>Resume Uploads (Optional)</legend>
          <div className="row mt-3">
            <div className="col-md-6">
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                multiple
                onChange={handleFileChange}
                className="form-control mb-2"
                disabled={uploading}
              />
            </div>
          </div>
          {resumeFiles.length > 0 && (
            <div className="row mt-3">
              <div className="col-md-8">
                <div className="text-success mb-2">Uploaded documents:</div>
                <ul className="list-group" style={{ maxWidth: "720px" }}>
                  {resumeFiles.map((file, index) => (
                    <li
                      key={file.id || `${file.filename}-${index}`}
                      className="list-group-item d-flex align-items-center justify-content-between gap-3"
                    >
                      <a
                        href={`${BASE}/uploads/resumes/${file.filename}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-truncate me-3"
                        style={{ minWidth: 0 }}
                      >
                        {file.originalName}
                      </a>
                      {file.id ? (
                        <button
                          type="button"
                          className="btn btn-link btn-sm text-danger p-0 flex-shrink-0"
                          onClick={() => handleRemoveResume(file.id)}
                          aria-label={`Remove ${file.originalName}`}
                          title="Remove"
                          style={{
                            width: "28px",
                            height: "28px",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <FaTrashAlt />
                        </button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </fieldset>

        <div className="d-flex justify-content-between">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleLeaveApply}
          >
            ← Back
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
          >
            {submitting ? "Submitting..." : "Submit →"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ApplyJobPage;
